// Utility function to build a tree from the coreEntity.entities slice
import * as c from "../../../constants";
import _ from "lodash";
import {DetermineIssuesOnGivenEntity} from "../entities";

export const BuildTree = (slice) => {
    // Ensure entities exist
    const validSlice = slice || {};
    const {entities, ids} = validSlice;

    // Create an entity map and initialize children array
    const entityMap = ids.reduce((acc, id) => {
        acc[id] = { ...entities[id], children: [] };
        return acc;
    }, {});

    const rootNodes = [];

    // Populate the parent-child relationship using ids for controlled iteration
    ids.forEach((id) => {
        const entity = entityMap[id];
        if (entity && entity.context) { // Ensure entity exists and context is true
            if (entity.parententityid) {
                const parent = entityMap[entity.parententityid];
                if (parent) {
                    parent.children.push(entity);
                }
            } else {
                rootNodes.push(entity); // No parent implies it's a root node
            }
        }
    });

    return rootNodes[0]; // Return the first root node as specified
};

export function findNearestFacilityGroupFromEntity(entities, entityid) {
    let current = entities[entityid];
    let lastFound = null;
    while (current) {
        if (current.typeId === c.ENTITY_TYPE.FacilityGroup) {
            lastFound = current;
        }
        current = entities[current.parententityid]; // Move up the hierarchy
    }

    return lastFound;
}

export function findNearestFacilityFromEntity(entities, entityid) {
    let current = entities[entityid];
    let lastFound = null;
    while (current) {
        if (
            'typeid' in current && (current.typeid === c.ENTITY_TYPE.Facility || current.typeid === c.ENTITY_TYPE.ValetArea) ||
            'typeId' in current && (current.typeId === c.ENTITY_TYPE.Facility || current.typeId === c.ENTITY_TYPE.ValetArea)
        ) {
            lastFound = current;
        }
        current = entities[current?.parentEntityId ?? current?.parententityid]; // Move up the hierarchy
    }
    return lastFound;
}

export const findEntityForTree = (entities, entityId) => {
    const entity = entities[entityId];
    if(!entity){
        return null;
    }
    const hasChildren = Object.values(entities).some((e) => e.parententityid === entity?.entityid);
    const deviceMode = _.find(entity?.settings ?? [], { name: "devicemode" })?.value ?? null;
    const disabled = deviceMode?.toLowerCase() === "door"
        ? String(_.find(entity?.settings ?? [], { name: "devicedisabled" })?.value) === "true"
        : entity?.state?.Disabled ?? false;
    return {
        parentEntityID: entity?.parententityid,
        entityID: entity?.entityid,
        name: entity?.name,
        typeID: entity?.typeid,
        hasChildren,
        lotFull:
            _.find(entity?.settings ?? [], { name: "displaylotfull" })?.value ==
            "auto"
                ? entity?.state != null && entity?.state?.Occupancy?.Full === true //if auto, is occupancy full or not?
                : _.find(entity?.settings ?? [], { name: "displaylotfull" })
                    ?.value == "on"
                    ? true
                    : false, //if manual, is it on or not?
        laneClosed:
            String(_.find(entity?.settings ?? [], { name: "laneclosedsign" })?.value) === "true",
        disabled,
        deviceMode,
        reverseOffline: entity?.state?.ReversingLane?.ReverseOffline,
        context: entity?.context ?? false
    };
}

export const DetermineErrorClassesByPeripheralType = (
    _entity,
    _peripheralType
) => {
    let _classList = [];
    let _listByPeripheral =
        DetermineIssuesOnGivenEntity(_entity).filter(
            (e) => e.Type === _peripheralType
        ) ?? [];
    _listByPeripheral.map((_error) => {
        if (_error.Severity === c.SEVERITY.LOW) _classList.push("low");
        if (_error.Severity === c.SEVERITY.WARNING) _classList.push("warning");
        if (_error.Severity === c.SEVERITY.CRITICAL) _classList.push("critical");
        if (_error.Severity === c.SEVERITY.WORLD_IS_ON_FIRE)
            _classList.push("fire");
    });
    _classList.push("norm"); //just to test we can get multiples (sim printer / printer dsd not quite right)
    return _.uniq(_classList);
};
export const SEVERITYSIDEPANEL = {
    INFO: 0,
    LOW: 20,
    WARNING: 50,
    CRITICAL: 80,
    WORLD_IS_ON_FIRE: 99
};

export const buildErrors = (entity) => {
    let errorArray = [];

    if (entity.state == null) {
        errorArray.push({
            Type: c.ERROR_TYPES.Device,
            Severity: SEVERITYSIDEPANEL.CRITICAL,
            Message: "DEVICE NOT RESPONDING",
        });
        return errorArray; //if device isn't responding, dont bother testing everything else
    }


    if (entity?.state?.Peripherals?.CreditCardReader?.IsConfigured ?? false) {
        if (entity.state?.Peripherals?.CreditCardReader?.Tampered)
            errorArray.push({
                Type: c.ERROR_TYPES.CCReader,
                Severity: SEVERITYSIDEPANEL.WORLD_IS_ON_FIRE,
                Message: "CC Reader Detected Tampering",
            });
        if (entity.state?.Peripherals?.CreditCardReader?.NoCommunications)
            errorArray.push({
                Type: c.ERROR_TYPES.CCReader,
                Severity: SEVERITYSIDEPANEL.CRITICAL,
                Message: "CC Reader Not Found",
            });
        if (entity.state?.Peripherals?.CreditCardReader?.HostOffline)
            errorArray.push({
                Type: c.ERROR_TYPES.CCReader,
                Severity: SEVERITYSIDEPANEL.CRITICAL,
                Message: "CC Reader Host Offline",
            });
        if (entity.state?.Peripherals?.CreditCardReader?.ReaderOffline)
            errorArray.push({
                Type: c.ERROR_TYPES.CCReader,
                Severity: SEVERITYSIDEPANEL.CRITICAL,
                Message: "CC Reader Offline",
            });
    }

    if (entity?.state?.Peripherals?.BNR?.IsConfigured ?? false) {
        if (entity.state?.Peripherals?.BNR?.CashBoxFull)
            errorArray.push({
                Type: c.ERROR_TYPES.BNR,
                Severity: SEVERITYSIDEPANEL.WARNING,
                Message: "BNR Cash Box Full",
            });
        if (entity.state?.Peripherals?.BNR?.CashMachineStatus === 1)
            errorArray.push({
                Type: c.ERROR_TYPES.BNR,
                Severity: SEVERITYSIDEPANEL.WORLD_IS_ON_FIRE,
                Message: "BNR Requires Maintenance",
            });
        if (entity.state?.Peripherals?.BNR?.CashMachineStatus === 2)
            errorArray.push({
                Type: c.ERROR_TYPES.BNR,
                Severity: SEVERITYSIDEPANEL.WARNING,
                Message: "BNR Offline",
            });
    }

    //loops
    if (entity?.state?.Peripherals?.Gate?.IsConfigured ?? false) {
        if (entity.state?.Peripherals?.Gate?.ArmingLoop === c.LOOP_STATUS.Unset) {
            errorArray.push({
                Type: c.ERROR_TYPES.ArmingLoop,
                Severity: SEVERITYSIDEPANEL.LOW,
                Message: "Arming Loop Unknown",
            });
        }
        if (entity.state?.Peripherals?.Gate?.ClosingLoop === c.LOOP_STATUS.Unset) {
            errorArray.push({
                Type: c.ERROR_TYPES.ClosingLoop,
                Severity: SEVERITYSIDEPANEL.LOW,
                Message: "Closing Loop Unknown",
            });
        }
    }

    if (entity.state?.Peripherals?.Printer?.IsConfigured ?? false) {
        if (entity?.deviceMode === c.DEVICE_MODE.ENTRY && !entity?.rateid) {
            errorArray.push({
                Type: c.ERROR_TYPES.Rate,
                Severity: SEVERITYSIDEPANEL.WARNING,
                Message: "NO RATE ASSIGNED",
            });
        }

        if (entity.state?.Peripherals?.Printer?.Low) {
            errorArray.push({
                Type: c.ERROR_TYPES.Printer,
                Severity: SEVERITYSIDEPANEL.LOW,
                Message: "Printer Paper Low",
            });
        }
        if (entity.state?.Peripherals?.Printer?.Jam) {
            errorArray.push({
                Type: c.ERROR_TYPES.Printer,
                Severity: SEVERITYSIDEPANEL.CRITICAL,
                Message: "Printer Jammed",
            });
        }
        if (entity.state?.Peripherals?.Printer?.Out) {
            errorArray.push({
                Type: c.ERROR_TYPES.Printer,
                Severity: SEVERITYSIDEPANEL.CRITICAL,
                Message: "Printer Out of Paper",
            });
        }
        if (!entity.state?.Peripherals?.Printer?.Online) {
            errorArray.push({
                Type: c.ERROR_TYPES.Printer,
                Severity: SEVERITYSIDEPANEL.CRITICAL,
                Message: "Printer Offline",
            });
        }
    }
    return errorArray;
};

